/**
 * The rest timer as a reducer (`D110`, `T48`): auto-started when a set is
 * logged, adjustable by ±15 s, skippable, and finished exactly once.
 *
 * ── TIME IS A PARAMETER ─────────────────────────────────────────────────────
 * No `Date.now()` in here — every action that needs the clock carries `now`
 * (epoch milliseconds), so the whole countdown is provable in a test without
 * waiting. The screen dispatches `tick` from an interval and passes the real
 * clock in; the reducer only ever does arithmetic on what it is handed.
 *
 * ── `finished` IS AN EDGE, NOT A STATE THE TIMER LINGERS IN ─────────────────
 * It becomes `true` only when a RUNNING timer's remaining time reaches zero —
 * that is the moment the vibration and the sound fire (`RestTimer` watches
 * it). `skip` deliberately does NOT set it: someone who skipped the rest asked
 * for silence, not for the end-of-rest chime. The next `start` clears it.
 */
/** What a plan item with no `restSeconds` of its own rests for. */
export const DEFAULT_REST_SECONDS = 90;
export const REST_TIMER_IDLE = {
    endsAtMs: null,
    finished: false,
    remainingMs: 0,
    running: false,
};
/** `endsAt - now`, floored at zero, plus the finished edge when it lands. */
function settle(endsAtMs, now) {
    const remainingMs = Math.max(0, endsAtMs - now);
    if (remainingMs === 0) {
        return { endsAtMs: null, finished: true, remainingMs: 0, running: false };
    }
    return { endsAtMs, finished: false, remainingMs, running: true };
}
export function restTimerReducer(state, action) {
    switch (action.type) {
        case "start": {
            const durationMs = Math.max(0, action.seconds) * 1000;
            return {
                endsAtMs: action.now + durationMs,
                finished: false,
                remainingMs: durationMs,
                running: durationMs > 0,
            };
        }
        case "adjust": {
            // Adjusting a timer that is not running is a tap on a dead control.
            if (!state.running || state.endsAtMs === null) {
                return state;
            }
            // `−15` past zero completes the rest rather than going negative — the
            // person said "I am done resting", which is what reaching zero means.
            return settle(state.endsAtMs + action.deltaSeconds * 1000, action.now);
        }
        case "skip":
            // Not `finished`: a skip asked for silence, not for the chime.
            return REST_TIMER_IDLE;
        case "tick": {
            if (!state.running || state.endsAtMs === null) {
                return state;
            }
            const next = settle(state.endsAtMs, action.now);
            // Identity for an unchanged countdown, so a fast interval does not
            // re-render a screen whose numbers did not move.
            return next.remainingMs === state.remainingMs && next.running
                ? state
                : next;
        }
        default:
            return state;
    }
}
/** `mm:ss` for the `Numeral` countdown — tabular digits do the aligning. */
export function formatCountdown(remainingMs) {
    const totalSeconds = Math.ceil(Math.max(0, remainingMs) / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
}
//# sourceMappingURL=rest-timer.js.map